import { useImageStoreRemote, SaveImageConfig } from "./use-image-store-remote";
import { useImageStoreLocal } from "./use-image-store-local";
import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { v4 as uuid } from "uuid";

export interface SaveImageAtURLConfig {
  url: string;
  name?: string;
  existingName?: string;
  isPublic: boolean;
  folder?: string;
  mimeTypeParam?: string;
}

export const useImageStore = () => {
  const { isUserAuthenticated } = useAuthContext();

  const {
    saveImage: saveImageRemote,
    resizeAndSaveImage: resizeAndSaveImageRemote,
    getImageUrl: getImageUrlRemote,
    duplicatePublicImage,
    getRenderedResultImageURL,
    getRenderedThumbnailImageURL,
    copyImageFromPublicToUser,
    duplicateUserImage,
    deleteImage,
    deletePointIconImage,
  } = useImageStoreRemote();

  const {
    getImage: getImageFromCache,
    saveImage: saveImageToCache,
    removeImage: removeImageFromCache,
    savePendingUpload,
    getPendingUploads,
    removePendingUpload,
    clearPendingUploads,
  } = useImageStoreLocal();

  // Wrapper for saveImage that queues uploads for guests
  const saveImage = async (config: SaveImageConfig): Promise<string> => {
    if (!isUserAuthenticated) {
      // Guest user - queue upload for after authentication
      const id = uuid();
      const mimeType = config.mimeTypeParam || config.image.type || "image/png";
      const extension = mimeType === "image/jpeg" ? "jpeg" : "png";
      const key = config.newFilename ?? id;

      // Save image to IndexedDB for immediate display
      await saveImageToCache(key, config.image);

      // Queue upload metadata
      await savePendingUpload({
        key,
        blob: config.image,
        folder: config.folder || "",
        projectID: "", // Will be set from context when processing
        extension,
        isPublic: config.isPublic,
      });

      console.log(`Queued image upload for guest: ${key}`);
      return id;
    }

    // Authenticated user - upload immediately
    return saveImageRemote(config);
  };

  // Wrapper for resizeAndSaveImage that queues uploads for guests
  const resizeAndSaveImage = async (
    config: SaveImageConfig,
    maxSize: number,
    mimeType: string
  ): Promise<string> => {
    if (!isUserAuthenticated) {
      // For guests, resize locally then queue
      // Note: We'll use browser-image-compression here too
      const imageCompression = (await import("browser-image-compression"))
        .default;
      const options = {
        maxWidthOrHeight: maxSize,
        useWebWorker: true,
        fileType: `image/${mimeType}`,
      };
      const compressedBlob = await imageCompression(
        config.image as File,
        options
      );

      const newConfig = {
        ...config,
        image: compressedBlob,
      };
      return saveImage(newConfig);
    }

    // Authenticated user - resize and upload immediately
    return resizeAndSaveImageRemote(config, maxSize, mimeType);
  };

  // Wrapper for getImageUrl that returns blob URLs from IndexedDB for guests
  const getImageUrl = async (
    id: string,
    extension: string,
    isPublic: boolean,
    folder?: string
  ): Promise<string | null> => {
    // Public images (templates, etc.) are always fetched from Supabase
    if (isPublic) {
      return getImageUrlRemote(id, extension, isPublic, folder);
    }

    if (!isUserAuthenticated) {
      // Guest user - try to get from IndexedDB cache (already returns blob URL)
      const blobUrl = await getImageFromCache(id);
      if (blobUrl) {
        console.log(`Retrieved guest image from cache: ${id} -> ${blobUrl}`);
        return blobUrl;
      }
      console.warn(`Guest image not found in cache: ${id}`);
      return null;
    }

    // Authenticated user - get from Supabase
    return getImageUrlRemote(id, extension, isPublic, folder);
  };

  const saveImageAtURL = async (config: SaveImageAtURLConfig) => {
    const { url, name, existingName, isPublic, folder, mimeTypeParam } = config;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch image from URL: ${url}`);
    }
    const blob = await response.blob();
    const newConfig = {
      image: blob,
      existingFilename: existingName,
      newFilename: name,
      isPublic,
      folder,
      mimeTypeParam,
    };
    return saveImage(newConfig);
  };

  // Process all pending uploads after authentication
  const processPendingUploads = async (
    projectID: string
  ): Promise<Record<string, string>> => {
    if (!isUserAuthenticated) {
      throw new Error("Cannot process pending uploads: User not authenticated");
    }

    const pendingUploads = await getPendingUploads();
    const keyMapping: Record<string, string> = {};

    console.log(`Processing ${pendingUploads.length} pending image uploads...`);

    for (const upload of pendingUploads) {
      try {
        const config: SaveImageConfig = {
          image: upload.blob,
          newFilename: upload.key,
          isPublic: upload.isPublic,
          folder: upload.folder || `projects/${projectID}`,
          mimeTypeParam: `image/${upload.extension}`,
        };

        // Upload to Supabase
        const newId = await saveImageRemote(config);

        // Map old key to new ID
        keyMapping[upload.key] = newId;

        // Remove from pending queue
        await removePendingUpload(upload.key);

        console.log(`Uploaded pending image: ${upload.key} -> ${newId}`);
      } catch (error) {
        console.error(`Failed to upload pending image ${upload.key}:`, error);
        // Continue with other uploads even if one fails
      }
    }

    console.log(`Completed processing pending uploads. Mapping:`, keyMapping);
    return keyMapping;
  };

  return {
    saveImage,
    saveImageAtURL,
    resizeAndSaveImage,
    getImageUrl,
    duplicatePublicImage,
    duplicateUserImage,
    getRenderedResultImageURL,
    getRenderedThumbnailImageURL,
    copyImageFromPublicToUser,
    deleteImage,
    deletePointIconImage,
    getImageFromCache,
    saveImageToCache,
    removeImageFromCache,
    getPendingUploads,
    clearPendingUploads,
    processPendingUploads,
  };
};
